import Flag from "react-world-flags";
import { Coins } from "lucide-react";
import { AccountCard } from "./AccountCard";
import { AddAccountPlaceholderCard } from "./AddAccountPlaceholderCard";
import type { AccountSectionDisplay } from "../types/accounts-wallets.types";
import { ISO3_TO_ISO2_MAP } from "../../dispersions/constants/dispersions.constants";
import useTranslations from "@/shared/lang/useTranslations";

const SECTION_CODE_TO_ISO2: Record<string, string> = {
  USA: "US",
  CHN: "CN",
  HKG: "HK",
  CRI: "CR",
  ...ISO3_TO_ISO2_MAP,
};

function getDisplayCount(section: AccountSectionDisplay, hasPlaceholder: boolean): number {
  return section.accountCount + (hasPlaceholder ? 1 : 0);
}

function getSectionFlagCode(sectionCode: string): string | null {
  if (sectionCode === "digital") return null;
  if (sectionCode.length === 2) return sectionCode;
  return SECTION_CODE_TO_ISO2[sectionCode] ?? sectionCode;
}

type AccountSectionProps = {
  section: AccountSectionDisplay;
  onAccountClick?: (id: string) => void;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string, name: string) => void;
  addPlaceholderLabel?: string | null;
  onAddPlaceholderClick?: () => void;
};

export const AccountSection = ({
  section,
  onAccountClick,
  onEditClick,
  onDeleteClick,
  addPlaceholderLabel,
  onAddPlaceholderClick,
}: AccountSectionProps) => {
  const { t } = useTranslations("accountsWallets");
  const hasPlaceholder = Boolean(addPlaceholderLabel);
  const displayCount = getDisplayCount(section, hasPlaceholder);
  const accountsLabel =
    displayCount === 1
      ? t("accounts_label_singular")
      : t("accounts_label_plural", { count: displayCount });

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3">
        {section.isDigital ? (
          <div className="size-10 rounded-full bg-slate-900 dark:bg-zinc-700 flex items-center justify-center text-white shrink-0">
            <Coins className="h-5 w-5" />
          </div>
        ) : (
          <div className="size-10 rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
            {getSectionFlagCode(section.code) && (
              <Flag
                code={getSectionFlagCode(section.code)!}
                className="w-10 h-6 object-cover"
                aria-hidden
              />
            )}
          </div>
        )}
        <h3 className="text-lg font-bold text-slate-800 dark:text-white">
          {section.title}
        </h3>
        <span className="bg-slate-200 dark:bg-zinc-600 text-slate-600 dark:text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded uppercase shrink-0">
          {accountsLabel}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {section.accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            onCardClick={onAccountClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
          />
        ))}
        {addPlaceholderLabel && (
          <AddAccountPlaceholderCard
            label={addPlaceholderLabel}
            onClick={onAddPlaceholderClick}
          />
        )}
      </div>
    </section>
  );
};
